/**
 * CR283: Snapshot Deal Decisions Hook
 * 
 * Manages per-snapshot Accept/Hide decisions for deals.
 * Default behavior: if no row exists, treat as ACCEPTED.
 */
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface DealDecision {
  deal_id: string;
  decision: "ACCEPTED" | "HIDDEN";
  reason: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface SnapshotDeal {
  id: string;
  dedupe_key: string;
  headline_mechanic: string | null;
  merchant_name: string | null;
  deal_type: string | null;
  valid_from: string | null;
  valid_until: string | null;
  visibility_status: string;
  computed_value_cad: number | null;
  decision: "ACCEPTED" | "HIDDEN";
}

export interface UseSnapshotDealDecisionsResult {
  deals: SnapshotDeal[];
  loading: boolean;
  error: string | null;
  // Counts
  totalDeals: number;
  acceptedCount: number;
  hiddenCount: number;
  expiredCount: number;
  activeCount: number;
  // Actions
  refetch: () => Promise<void>;
  toggleDecision: (dealId: string, newDecision: "ACCEPTED" | "HIDDEN", reason?: string) => Promise<boolean>;
  bulkSetDecision: (dealIds: string[], decision: "ACCEPTED" | "HIDDEN") => Promise<boolean>;
}

export function useSnapshotDealDecisions(snapshotId: string | null): UseSnapshotDealDecisionsResult {
  const [deals, setDeals] = useState<SnapshotDeal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDeals = useCallback(async () => {
    if (!snapshotId) {
      setDeals([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch deals in snapshot
      const { data: productionDeals, error: dealsError } = await supabase
        .from("cr075_production_deals")
        .select(`
          id,
          dedupe_key,
          valid_from,
          valid_until,
          visibility_status,
          computed_value_cad,
          canonical_deal_json
        `)
        .eq("admin_snapshot_id", snapshotId)
        .order("computed_value_cad", { ascending: false, nullsFirst: false });

      if (dealsError) throw new Error(dealsError.message);

      // Fetch existing decisions
      const { data: decisionsData, error: decisionsError } = await supabase
        .from("admin_snapshot_deal_decisions")
        .select("deal_id, decision, reason, updated_at, updated_by")
        .eq("snapshot_id", snapshotId);

      if (decisionsError) throw new Error(decisionsError.message);

      // Build decision lookup
      const decisionsMap = new Map<string, DealDecision>();
      for (const d of decisionsData || []) {
        decisionsMap.set(d.deal_id, d as DealDecision);
      }

      // Merge deals with decisions
      const mergedDeals: SnapshotDeal[] = (productionDeals || []).map((deal: any) => {
        const json = deal.canonical_deal_json || {};
        const existing = decisionsMap.get(deal.id);
        
        return {
          id: deal.id,
          dedupe_key: deal.dedupe_key,
          headline_mechanic: json.headline_mechanic || null,
          merchant_name: json.merchant_name || json.promo_merchant_name || null,
          deal_type: json.deal_type || null,
          valid_from: deal.valid_from,
          valid_until: deal.valid_until,
          visibility_status: deal.visibility_status,
          computed_value_cad: deal.computed_value_cad,
          decision: existing?.decision || "ACCEPTED",
        };
      });

      setDeals(mergedDeals);
    } catch (err) {
      console.error("[useSnapshotDealDecisions] Error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [snapshotId]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const toggleDecision = useCallback(async (
    dealId: string, 
    newDecision: "ACCEPTED" | "HIDDEN",
    reason?: string
  ): Promise<boolean> => {
    if (!snapshotId) return false;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error: upsertError } = await supabase
        .from("admin_snapshot_deal_decisions")
        .upsert({
          snapshot_id: snapshotId,
          deal_id: dealId,
          decision: newDecision,
          reason: reason || null,
          updated_by: user?.email || null,
        }, {
          onConflict: "snapshot_id,deal_id",
        });

      if (upsertError) {
        console.error("[useSnapshotDealDecisions] Toggle error:", upsertError);
        toast.error(`Failed to update decision: ${upsertError.message}`);
        return false;
      }

      // Update local state
      setDeals(prev => prev.map(d => 
        d.id === dealId ? { ...d, decision: newDecision } : d
      ));

      toast.success(`Deal ${newDecision === "HIDDEN" ? "hidden" : "accepted"}`);
      return true;
    } catch (err) {
      console.error("[useSnapshotDealDecisions] Error:", err);
      toast.error("Failed to update decision");
      return false;
    }
  }, [snapshotId]);

  const bulkSetDecision = useCallback(async (
    dealIds: string[], 
    decision: "ACCEPTED" | "HIDDEN"
  ): Promise<boolean> => {
    if (!snapshotId || dealIds.length === 0) return false;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const rows = dealIds.map(dealId => ({
        snapshot_id: snapshotId,
        deal_id: dealId,
        decision,
        updated_by: user?.email || null,
      }));

      const { error: upsertError } = await supabase
        .from("admin_snapshot_deal_decisions")
        .upsert(rows, { onConflict: "snapshot_id,deal_id" });

      if (upsertError) {
        console.error("[useSnapshotDealDecisions] Bulk error:", upsertError);
        toast.error(`Failed to update decisions: ${upsertError.message}`);
        return false;
      }

      // Update local state
      const idSet = new Set(dealIds);
      setDeals(prev => prev.map(d => 
        idSet.has(d.id) ? { ...d, decision } : d
      ));

      toast.success(`${dealIds.length} deals ${decision === "HIDDEN" ? "hidden" : "accepted"}`);
      return true;
    } catch (err) {
      console.error("[useSnapshotDealDecisions] Error:", err);
      toast.error("Failed to update decisions");
      return false;
    }
  }, [snapshotId]);

  // Compute counts
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const totalDeals = deals.length;
  const acceptedCount = deals.filter(d => d.decision === "ACCEPTED").length;
  const hiddenCount = deals.filter(d => d.decision === "HIDDEN").length;
  const expiredCount = deals.filter(d => {
    if (!d.valid_until) return false;
    return new Date(d.valid_until) < now;
  }).length;
  const activeCount = totalDeals - expiredCount;

  return {
    deals,
    loading,
    error,
    totalDeals,
    acceptedCount,
    hiddenCount,
    expiredCount,
    activeCount,
    refetch: fetchDeals,
    toggleDecision,
    bulkSetDecision,
  };
}
